<template>
  <Modal
    :show="isModalOpen"
    container-class="atb-w-1/3"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="closeDeploySystemModalHandler"
    :is-closable="showCloseModal"
  >
    <template #header>
      {{
        showDownloadProgress ? $t('deployingSystems') : $t('systemDeployTitle')
      }}
    </template>
    <template #body>
      <div
        v-if="showDownloadProgress"
        class="atb-w-full"
      >
        <ProgressBar
          :message="progressMessage"
          :default-message="$t('systemDeploymentMessage')"
          :percent="progressPercent"
        />
      </div>
      <div
        v-show="isDeploy1stwindow && !showDownloadProgress"
        class="atb-flex atb-flex-col atb-gap-4"
      >
        <div class="eds-table-wrap atb-w-full atb-h-48">
          <DataGrid
            :column-defs="systemDefs"
            :row-data="systemsStore.clientSystems.content"
            :row-selection="'multiple'"
            :suppress-row-click-selection="true"
            :pagination="false"
            :get-row-node-id="getRowNodeId"
            :on-data-grid-ready="onGridReady"
            :on-row-selected="onRowSelected"
            :row-multi-select-with-click="true"
          />
        </div>
      </div>
      <div
        v-show="!isDeploy1stwindow && !showDownloadProgress"
        class="atb-flex atb-flex-col atb-gap-4"
      >
        <Alert
          :show="showNotification"
          motif="error"
          shadow
          border
          @close="showNotification = false"
        >
          <template #title>{{ $t('error') }} </template>
          <template #message>
            {{ $t('deploymentNameInvalid') }}
          </template>
        </Alert>
        <FormField
          :label="$t('deploymentName')"
          form-field-class="atb-w-full"
          required="true"
        >
          <Textbox
            id="systemDeployDeploymentNameTextBox"
            v-model="deploymentName"
            :state="inputError ? 'error' : 'basic'"
            :label="$t('deploymentName')"
            @update:modelValue="validateDeploymentNameHandler"
          />
        </FormField>
        <div class="atb-pb-4">
          <Checkbox
            id="exportsystems"
            :label="$t('exportSystems')"
            name="exportSystemsCheckbox"
            v-model:checked="exportSystemsChecked"
          />
        </div>

        <div class="eds-table-wrap atb-w-full atb-h-48">
          <DataGrid
            :column-defs="dataSourceDefs"
            :row-data="client.dataSources"
            :row-selection="'multiple'"
            :suppress-row-click-selection="true"
            :pagination="false"
            :get-row-node-id="getDatasourceId"
            :on-data-grid-ready="onDatasourceGridReady"
            :on-row-selected="onDataSourceRowSelected"
            :row-multi-select-with-click="true"
          />
        </div>
      </div>
    </template>
    <template
      v-if="!showDownloadProgress"
      #footer
    >
      <div class="atb-flex atb-gap-4">
        <Button
          motif="basic"
          @click="closeDeploySystemModalHandler"
          >{{ $t('cancel') }}</Button
        >
        <Button
          v-show="displayBackButton"
          @click="navigateToFirstWindow"
          >{{ $t('back') }}</Button
        >
        <Button
          id="systemsDeployNextButton"
          v-show="isDeploy1stwindow"
          :disabled="disableNextButton"
          @click="navigateToSecondWindow"
          >{{ $t('next') }}</Button
        >
        <Button
          id="systemsDeployOkButton"
          v-show="!isDeploy1stwindow"
          type="submit"
          :disabled="disableOkButton"
          @click="downloadDeployment"
          >{{ $t('ok') }}</Button
        >
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
  import { onMounted, Ref, ref, toRefs } from 'vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Alert from '@atb-ui/eds-vue/src/components/Alert.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import { AgGridEvent, ColumnApi, GridApi } from 'ag-grid-community';
  import { useI18n } from 'vue-i18n';
  import DataGrid from '@/app/shared/components/data-grid/DataGrid.vue';
  import ProgressBar from '@/app/shared/components/ProgressBar.vue';
  import Checkbox from '@atb-ui/eds-vue/src/components/Checkbox.vue';
  import { DEPLOYMENT_ALPHANUMERIC_REGEX } from '@/app/utils/regexpr-const';
  import systemsApi from '@/app/features/ams/systems/systems-service';
  import type { DeploymentProgressEvent } from '@/app/features/ams/systems/systems-service';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useSystemsStore } from '@/app/features/ams/systems/systems-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import DomPurify from 'dompurify';
  import { useModal } from '@/app/shared/hooks/use-modal';

  interface DeployEvent {
    (_event: 'closeSystemActionPopup'): void;
    (_event: 'deploySystem'): void;
  }

  interface DeployProps {
    systemId?: string | number;
    systemName?: string;
  }

  const emit = defineEmits<DeployEvent>();
  const props = withDefaults(defineProps<DeployProps>(), {
    systemId: -1,
  });
  const deploymentName = ref('');
  const showNotification = ref(false);
  const disableNextButton = ref(true);
  const disableOkButton = ref(true);
  const displayBackButton = ref(false);
  const inputError = ref(false);
  const { t } = useI18n();
  const { isModalOpen, changeModalVisibility } = useModal();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  const authStore = useAuthStore();

  const client = clientStore.getSelectedClient;
  const systemsStore = useSystemsStore();
  const { systemId, systemName } = toRefs(props);

  const gridApi: Ref<GridApi | null> = ref(null);

  const datasourceGridApi: Ref<GridApi | null> = ref(null);
  const datasourceColumnApi: Ref<ColumnApi | null> = ref(null);

  const exportSystemsChecked = ref(false);
  const showCloseModal = ref(true);
  const progressPercent = ref(0);
  const progressMessage = ref('');
  const showDownloadProgress = ref(false);

  const systemDefs = [
    {
      field: 'name',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'systemName',
      headerName: t('systemName'),
      suppressSizeToFit: false,
    },
    {
      field: 'notes',
      headerName: t('notes'),
      suppressSizeToFit: false,
    },
  ];

  const dataSourceDefs = [
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('dataSourceName'),
      suppressSizeToFit: false,
    },
  ];

  const onGridReady = (params: AgGridEvent) => {
    gridApi.value = params.api;
    gridApi.value.sizeColumnsToFit();
  };

  const onDatasourceGridReady = (params: AgGridEvent) => {
    datasourceGridApi.value = params.api;
    datasourceColumnApi.value = params.columnApi;
    datasourceGridApi.value.sizeColumnsToFit();
  };

  const getRowNodeId = (data: any) => {
    return data.id;
  };

  const getDatasourceId = (data: any) => {
    return data.id;
  };
  const isDeploy1stwindow = ref(true);
  const selectedSystems: Ref<any[]> = ref([]);
  const selectedDataSources: Ref<any[]> = ref([]);

  const onRowSelected = () => {
    const selectedData = gridApi.value?.getSelectedNodes();
    selectedSystems.value =
      selectedData?.map((node) => node.data.systemName) || [];
    if (selectedSystems.value.length > 0) {
      disableNextButton.value = false;
    } else {
      disableNextButton.value = true;
    }
  };

  const onDataSourceRowSelected = () => {
    const selectedData = datasourceGridApi.value?.getSelectedNodes();
    selectedDataSources.value =
      selectedData?.map((node) => node.data.name) || [];
    if (
      deploymentName.value.length > 0 &&
      validDeploymentName() &&
      selectedDataSources.value.length > 0
    ) {
      disableOkButton.value = false;
    } else {
      disableOkButton.value = true;
    }
  };

  const closeDeploySystemModalHandler = (): void => {
    changeModalVisibility();
    isDeploy1stwindow.value = true;
    displayBackButton.value = false;
    gridApi.value?.deselectAll();
    datasourceGridApi.value?.deselectAll();
  };

  const navigateToSecondWindow = (): void => {
    isDeploy1stwindow.value = false;
    displayBackButton.value = true;
    datasourceGridApi.value?.sizeColumnsToFit();
  };

  const navigateToFirstWindow = (): void => {
    isDeploy1stwindow.value = true;
    displayBackButton.value = false;
  };

  const validateDeploymentNameHandler = (): void => {
    if (validDeploymentName()) {
      showNotification.value = false;
      inputError.value = false;
      onDataSourceRowSelected();
    } else {
      showNotification.value = true;
      inputError.value = true;
      disableOkButton.value = true;
    }
  };

  const validDeploymentName = (): boolean => {
    let isDeploymentNameValid = true;
    if (deploymentName.value.length) {
      const nameValidationResult = ref(
        new RegExp(DEPLOYMENT_ALPHANUMERIC_REGEX).test(deploymentName.value)
      );
      isDeploymentNameValid = nameValidationResult.value;
    }
    return isDeploymentNameValid;
  };

  onMounted(() => {
    if (systemId.value === -1) {
      isDeploy1stwindow.value = true;
    } else {
      isDeploy1stwindow.value = false;
      displayBackButton.value = false;
    }
  });

  const downloadDeployment = (): void => {
    const deploymentInfo = {
      clientName: client.clientName,
      deploymentName: deploymentName.value,
      clientSystems:
        selectedSystems.value.length > 0
          ? selectedSystems.value
          : [systemName?.value],
      clientDataSources: selectedDataSources.value,
      isExport: exportSystemsChecked.value,
    };

    dowloadDeploymentFile(deploymentInfo);
  };

  const dowloadDeploymentFile = async (deploymentInfo: any) => {
    try {
      showCloseModal.value = false;
      showDownloadProgress.value = true;
      progressPercent.value = 0;
      progressMessage.value = '';
      authStore.preventExpiryWarning = true;

      const { blob, fileName } = await systemsApi.streamDeploymentFile(
        client.id,
        deploymentInfo,
        (event: DeploymentProgressEvent) => {
          progressMessage.value = event.message;
          if (event.total > 0) {
            progressPercent.value = Math.round(
              (event.current / event.total) * 100
            );
          }
        }
      );

      const fileURL = URL.createObjectURL(blob);
      const fileLink = document.createElement('a');

      fileLink.href = DomPurify.sanitize(fileURL);
      fileLink.download = DomPurify.sanitize(fileName);
      document.body.appendChild(fileLink);
      fileLink.click();

      changeModalVisibility();
      emit('closeSystemActionPopup');
      resetModel();
      authStore.preventExpiryWarning = false;
      alertStore.showNotification(
        t('The deployment was successfully downloaded'),
        t('Systems'),
        Motif.SUCCESS
      );
    } catch (error: any) {
      showCloseModal.value = true;
      showDownloadProgress.value = false;
      progressPercent.value = 0;
      progressMessage.value = '';
      const errorMessage =
        error?.message || 'Error downloading deployment file';
      alertStore.showNotification(t(errorMessage), t('Systems'), Motif.ERROR);
    }
  };

  const resetModel = (): void => {
    isDeploy1stwindow.value = true;
    disableNextButton.value = true;
    disableOkButton.value = false;
    displayBackButton.value = false;
    showDownloadProgress.value = false;
    progressPercent.value = 0;
    progressMessage.value = '';
  };

  defineExpose({ changeModalVisibility });
</script>
